import AgTechDataActionSurface, { AgTechDataActionSurfaceConfirmationProps } from "@agtech/core/data/actions/AgTechDataActionSurfaces";
import { AgTechDataActionEventHandlers } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechFormContext } from "@agtech/core/forms/AgTechFormContext";
import { AgTechFormActionProps } from "@agtech/core/forms/AgTechFormProps";
import { logDev } from "@agtech/core/logging/AgTechLogger";
import { agtechDelay, executeWithMinimumDelay, isDev } from "@agtech/core/utilities/AgTechUtilities";
import React, { ReactNode, useRef, useState } from "react";
import { AgTechButton } from "../../Buttons/AgTechButtons";
import { AgTechForm } from "../../Forms/AgTechForm";
import AgTechHeaderDetail, { AgTechHeaderDetailProps } from "../../Headers/AgTechHeaderDetail";
import { isTitleDetail } from "../../Headers/AgTechTitleDetailHeader";
import AgTechDialog from "./AgTechDialog";
import AgTechFormDialogDevelopmentPane from "./Development/AgTechFormDialogDevelopmentPane";
import { AgTechQueryRun } from "@agtech/core/data/queries/AgTechDataQueries";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import { useDialogContext } from "@agtech/core/forms/dialogs/AgTechDialogs";

export type AgTechFormDialogProps<TQueryData, TFormData, TActionData, TResponseData, TContextData = TFormData> = {
    width: number,
    dialogData: AgTechFormDialogDataProps<TQueryData, TFormData, TContextData>,
    dialogAction: AgTechFormActionProps<TFormData, TActionData, TResponseData, TContextData>,
    structure: (data: AgTechFormDialogData<TFormData, TContextData>) => AgTechFormDialogStructureProps,
    dialogBehavior?: (data: AgTechFormDialogData<TFormData, TContextData>) => AgTechFormDialogBehaviorProps<TActionData, TResponseData>,
    children: (context: AgTechFormContext<TFormData, TContextData>) => ReactNode,
}

export type AgTechFormDialogDataProps<TQueryData, TFormData, TContextData = TFormData> = {
    query: AgTechQueryRun<TQueryData>,
    getDialogData: (formQueryData: TQueryData) => TFormData,
    getDialogContext?: (formQueryData: TQueryData) => TContextData
}

export type AgTechFormDialogData<TFormData, TContextData = TFormData> = {
    formData: TFormData,
    context: TContextData
}

export type AgTechFormDialogStructureProps = {
    header: string | AgTechHeaderDetailProps,
    bodyClasses?: string,
    submitButtonText?: string,
    cancelButtonText?: string,
}

export type AgTechFormDialogBehaviorProps<TActionData, TResponseData> = {
    confirmation?: AgTechFormDialogConfirmationProps<TActionData, TResponseData>,
    eventHandlers?: AgTechFormDialogEventHandlers<TResponseData>,
    refreshWhenChanged?: boolean
}

export type AgTechFormDialogEventHandlers<TDialogOutput> = {
    onDialogSubmitted?: (dialogOutput: TDialogOutput) => Promise<void>,
    onDialogClosed?: () => Promise<void>
}

export type AgTechFormDialogActions = {
    close: () => void,
    grow: (updatedWidth: number) => Promise<void>
}

export type AgTechFormDialogConfirmationProps<TActionData, TResponseData> = {
    renderFormDialogConfirmationView: (
        confirmationData: AgTechDataActionSurfaceConfirmationProps<TActionData, TResponseData>,
        actions: AgTechFormDialogActions
    ) => Promise<React.ReactNode | undefined>
}

const AgTechFormDialogComponent = <TQueryData, TFormData, TActionData, TResponseData, TContextData = TFormData>(
    props: AgTechFormDialogProps<TQueryData, TFormData, TActionData, TResponseData, TContextData>
) => {
    const [dialogWidth, setDialogWidth] = useState(props.width);
    const dialogLoggingData: any = {};

    return (
        <AgTechDialog width={dialogWidth}>
            <AgTechQueryLoadingSurface<TQueryData, AgTechFormDialogData<TFormData, any>>
                query={props.dialogData.query}
                projection={queryData => {
                    dialogLoggingData.queryData = queryData;

                    const dialogData = props.dialogData.getDialogData(queryData);
                    const dialogContext = (props.dialogData.getDialogContext ?? props.dialogData.getDialogData)(queryData);

                    return {
                        formData: dialogData,
                        context: dialogContext
                    }
                }}
            >
                {(data) => {
                    const dialogStructure = props.structure(data);

                    setTimeout(() => logDev('[Opened Form Dialog]', data), 200);

                    return (
                        <AgTechFormDialogSurface
                            initialFormData={data.formData}
                            contextualData={data.context}
                            structure={dialogStructure}
                            behavior={props.dialogBehavior?.(data)}
                            dialogAction={props.dialogAction}
                            grow={(width) => executeWithMinimumDelay({
                                action: async () => {
                                    setDialogWidth(width);
                                },
                                minimumDelay: 1000
                            })}
                            
                        >
                            {(formContext) => (
                                <>
                                    {props.children(formContext)}
                                    {isDev() && (
                                        <AgTechFormDialogDevelopmentPane
                                            formContext={formContext}
                                            formActionData={props.dialogAction.getActionData}
                                        />
                                    )}
                                </>
                            )}
                        </AgTechFormDialogSurface>
                    )
                }}
            </AgTechQueryLoadingSurface>
        </AgTechDialog>
    );
};

type AgTechFormDialogSurfaceProps<TFormData, TActionData, TResponseData, TContextData = TFormData> = {
    initialFormData: TFormData,
    contextualData: TContextData,
    dialogAction: AgTechFormActionProps<TFormData, TActionData, TResponseData, TContextData>,
    structure: AgTechFormDialogStructureProps,
    behavior?: AgTechFormDialogBehaviorProps<TActionData, TResponseData>,
    children: (context: AgTechFormContext<TFormData, TContextData>) => ReactNode,
    refreshWhenChanged?: boolean,
    grow: (updatedWidth: number) => Promise<void>
}

export const AgTechFormDialogSurface = <TFormData, TActionData, TResponseData, TContextData = TFormData>(
    props: AgTechFormDialogSurfaceProps<TFormData, TActionData, TResponseData, TContextData>
) => {
    const formDialog = useAgTechFormDialog({
        formAction: props.dialogAction,
        behavior: props.behavior
    });

    return (
        <AgTechDataActionSurface
            action={props.dialogAction.action}
            behavior={{
                eventHandlers: formDialog.dialogDataActionEventHandlers,
                confirmation: {
                    renderActionConfirmationView: async (data) => {
                        return await props.behavior?.confirmation?.renderFormDialogConfirmationView?.(data, {
                            close: formDialog.closeFormDialog,
                            grow: props.grow
                        });
                    }
                }
            }}
        >
            {() => (
                <>
                    <div className="px-4 py-[14px] text-plus border-b font-semibold bg-slate-100 flex">
                        {isTitleDetail(props.structure.header) ? (
                            <AgTechHeaderDetail
                                {...props.structure.header}
                                settings={{
                                    header: {
                                        classes: '!text-md',
                                        ...props.structure.header.settings?.header
                                    },
                                    detail: {
                                        classes: '!mt-2 !text-less'
                                    },
                                    ...props.structure.header?.settings
                                }}
                            />
                        ) : props.structure.header}
                    </div>
                    <div className="flexed relative overflow-hidden flex">
                        <div className={`stretched scrollable flex ${props.structure.bodyClasses ?? ''}`}>
                            <AgTechForm
                                initialFormData={props.initialFormData}
                                contextualData={props.contextualData as any}
                                action={props.dialogAction}
                                structure={{
                                    submitButton: formDialog.dialogSubmissionButton,
                                    classes: 'pt-2 px-4'
                                }}
                                behavior={{
                                    refreshWhenChanged: props.refreshWhenChanged
                                }}
                            >
                                {props.children}
                            </AgTechForm>
                        </div>
                    </div>
                    <div className="agtech-dialog-footer">
                        <AgTechButton
                            text={props.structure.cancelButtonText ?? 'Cancel'}
                            icon="fas fa-times"
                            classes='px-6 border-gray-400'
                            action={formDialog.closeFormDialog}
                        />
                        <AgTechButton
                            text={props.structure.submitButtonText ?? 'Submit'}
                            icon="fas fa-check"
                            classes='ml-auto px-6 button-highlight'
                            action={() => formDialog.dialogSubmissionButton.current?.click()}
                        />
                    </div>
                </>
            )}
        </AgTechDataActionSurface>
    )
}

export type AgTechFormDialogType<TActionData, TResponseData> = {
    dialogSubmissionButton: React.RefObject<{ click: () => Promise<void> }>,
    dialogDataActionEventHandlers: AgTechDataActionEventHandlers<TActionData, TResponseData>,
    closeFormDialog: () => Promise<void>
}

export const useAgTechFormDialog = <TFormData, TActionData, TResponseData, TContextData = TFormData>(props: {
    formAction: AgTechFormActionProps<TFormData, TActionData, TResponseData, TContextData>,
    behavior?: AgTechFormDialogBehaviorProps<TActionData, TResponseData>,
}): AgTechFormDialogType<TActionData, TResponseData> => {
    const dialogContext = useDialogContext();

    const submissionButton = useRef<{ click: () => Promise<void> }>({
        click: async () => {}
    });

    const doesDialogShowConfirmationPane = props.behavior?.confirmation?.renderFormDialogConfirmationView !== undefined;

    const dialogDataActionEventHandlers: AgTechDataActionEventHandlers<TActionData, TResponseData> = {
        onExecutionSuccessful: async actionProps => {
            const doesDialogShowConfirmationMessage =
                actionProps.actionConfiguration.showConfirmationMessage ?? true;

            if (doesDialogShowConfirmationMessage && !doesDialogShowConfirmationPane) {
                await agtechDelay(actionProps.actionConfiguration.actionConfirmationDelay ?? 400);
            }
        },
        onExecutionCompleted: async result => {
            if (!props.behavior?.confirmation) {
                await closeFormDialog();
            }

            if (props.behavior?.eventHandlers?.onDialogSubmitted && result.success) {
                logDev('Executing success handler');
                await props.behavior?.eventHandlers.onDialogSubmitted(result.success.data);
            }
        }
    }

    const closeFormDialog = async () => {
        if (props.behavior?.eventHandlers?.onDialogClosed) {
            await props.behavior?.eventHandlers.onDialogClosed();
        }
        
        dialogContext.closeDialog(null);
    }

    return {
        dialogSubmissionButton: submissionButton,
        dialogDataActionEventHandlers,
        closeFormDialog
    }
}

const AgTechFormDialog = React.memo(AgTechFormDialogComponent, (prev, curr) => {
    return prev.dialogData.query.queryResult.data === curr.dialogData.query.queryResult.data;
}) as typeof AgTechFormDialogComponent;

export default AgTechFormDialog;